import { MK_CLUB_TIME_ZONE } from '../constants/core'
import { MILLISECONDS_PER_DAY } from './formattingUtils'

/** Milliseconds in one day. Re-exported from formattingUtils for date calculations. */
export const MS_PER_DAY = MILLISECONDS_PER_DAY

export const isDateWithinRange = (date: Date, startDate: Date, endDate: Date): boolean => date >= startDate && date <= endDate

/**
 * Compares two dates by calendar day only (year, month, day), ignoring time.
 *
 * @param date1 First date
 * @param date2 Second date
 * @returns true if both dates represent the same calendar day in local timezone
 */
export const isSameDay = (date1: Date, date2: Date): boolean =>
  date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate()

/**
 * Returns a new Date with the same calendar day but time set to midnight (00:00:00.000).
 *
 * @param date The date to normalize
 * @returns New Date at midnight in local timezone
 */
export const normalizeDateToMidnight = (date: Date): Date => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

/**
 * Checks if two date ranges overlap (at least one day in common).
 * Dates are compared at midnight for consistent day-based comparison.
 *
 * @param checkIn1 Start of first range
 * @param checkOut1 End of first range
 * @param checkIn2 Start of second range
 * @param checkOut2 End of second range
 * @returns true if the ranges overlap
 */
export const doDateRangesOverlap = (checkIn1: Date, checkOut1: Date, checkIn2: Date, checkOut2: Date): boolean => {
  const range1Start = normalizeDateToMidnight(checkIn1)
  const range1End = normalizeDateToMidnight(checkOut1)
  const range2Start = normalizeDateToMidnight(checkIn2)
  const range2End = normalizeDateToMidnight(checkOut2)
  return (range1Start >= range2Start && range1Start <= range2End) || (range1End >= range2Start && range1End <= range2End) || (range1Start <= range2Start && range1End >= range2End)
}

/**
 * Converts a calendar date selection to a Date object representing midnight in the club timezone.
 * When a user clicks a date in the calendar (e.g., 11/1), this ensures it's interpreted
 * as 11/1 midnight in the club timezone, regardless of the user's local timezone.
 *
 * This should ONLY be called at the point where dates from PrimeReact calendar are first accessed.
 *
 * Example: If a west coast user clicks 11/1 at 11p PT, it should be 11/1 ET (not 10/31).
 * If a London user clicks 11/1 at 10p, it should be 11/1 ET (not 11/2).
 *
 * @param date The Date object from calendar selection (uses year/month/day components only)
 * @returns Date object representing midnight in the club timezone on the selected date
 */
export const convertCalendarDateToClubTimeZone = (date: Date): Date => {
  if (!date) return date

  // Extract year, month, day from the calendar selection
  // These represent the date the user clicked, independent of their timezone
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()

  const testDate = new Date(Date.UTC(year, month, day, 0, 0, 0))
  const offsetMinutes = getClubTimeZoneOffsetMinutes(testDate)
  if (offsetMinutes === null) {
    // Fallback: use -5 hours (EST)
    return new Date(Date.UTC(year, month, day, 5, 0, 0))
  }

  // If club timezone is GMT-5, midnight there is 05:00 UTC (+300 minutes).
  return new Date(Date.UTC(year, month, day, 0, -offsetMinutes, 0))
}

/**
 * Formats a Date object to YYYY-MM-DD string in the club timezone.
 * This ensures all dates are interpreted in the club timezone regardless of user's local timezone.
 *
 * @param date The Date object to format
 * @returns Date string in YYYY-MM-DD format in the club timezone
 */
export const formatDateInClubTimeZone = (date: Date): string => {
  if (!date) return ''

  // Format the date in the club timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: MK_CLUB_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

  const parts = formatter.formatToParts(date)
  const year = parts.find((p) => p.type === 'year')?.value
  const month = parts.find((p) => p.type === 'month')?.value
  const day = parts.find((p) => p.type === 'day')?.value

  return `${year}-${month}-${day}`
}

const parseOffsetMinutes = (timeZoneName: string): number | null => {
  const normalizedName = timeZoneName.replace('UTC', 'GMT').replace(/−/g, '-')
  if (normalizedName === 'GMT') return 0

  const offsetMatch = normalizedName.match(/^GMT([+-])(\d{1,2})(?::?(\d{2}))?$/)
  if (!offsetMatch) return null

  const sign = offsetMatch[1] === '+' ? 1 : -1
  const offsetHours = parseInt(offsetMatch[2], 10)
  const offsetMinutes = offsetMatch[3] ? parseInt(offsetMatch[3], 10) : 0

  return sign * (offsetHours * 60 + offsetMinutes)
}

const getClubTimeZoneOffsetMinutes = (testDate: Date): number | null => {
  try {
    const timezoneOffset =
      new Intl.DateTimeFormat('en-US', {
        timeZone: MK_CLUB_TIME_ZONE,
        timeZoneName: 'shortOffset',
      })
        .formatToParts(testDate)
        .find((p) => p.type === 'timeZoneName')?.value || 'GMT-5'

    return parseOffsetMinutes(timezoneOffset)
  } catch {
    return null
  }
}

/**
 * Parses a YYYY-MM-DD date string as a Date in the club timezone.
 * Creates a Date object representing midnight in the club timezone on the given date.
 *
 * @param dateStr Date string in YYYY-MM-DD format
 * @returns Date object at midnight in the club timezone, or null if invalid
 */
export const parseDateInClubTimeZone = (dateStr: string): Date | null => {
  if (!dateStr || typeof dateStr !== 'string') return null

  const dateOnlyMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!dateOnlyMatch) return null

  const year = parseInt(dateOnlyMatch[1], 10)
  const month = parseInt(dateOnlyMatch[2], 10)
  const day = parseInt(dateOnlyMatch[3], 10)

  const validationDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0))
  const isValidCalendarDate =
    validationDate.getUTCFullYear() === year && validationDate.getUTCMonth() === month - 1 && validationDate.getUTCDate() === day

  if (!isValidCalendarDate) return null

  // Determine club timezone offset for the specific calendar date (DST-aware).
  const testDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0))
  const offsetMinutes = getClubTimeZoneOffsetMinutes(testDate)

  if (offsetMinutes === null) {
    // Final fallback if timezone offset cannot be derived in this runtime.
    return new Date(Date.UTC(year, month - 1, day, 5, 0, 0))
  }

  const utcMinuteShift = -offsetMinutes
  return new Date(Date.UTC(year, month - 1, day, 0, utcMinuteShift, 0))
}

/**
 * Parses a YYYY-MM-DD date string into a local Date object.
 * Used for parsing date strings from API responses where dates should be treated as calendar dates.
 *
 * @param dateStr Date string in YYYY-MM-DD format
 * @returns Date object at local midnight, or null if invalid
 */
export const parseDateString = (dateStr: string): Date | null => {
  if (!dateStr || typeof dateStr !== 'string') return null
  const [year, month, day] = dateStr.split('-').map(Number)
  if (!year || !month || !day || isNaN(year) || isNaN(month) || isNaN(day)) return null
  const date = new Date(year, month - 1, day) // month is 0-indexed
  return isNaN(date.getTime()) ? null : date
}

/**
 * Converts a UTC date (representing midnight in club timezone) back to a local date
 * that represents the same calendar date. This is needed for PrimeReact Calendar
 * which expects dates in local timezone for its value prop.
 *
 * @param utcDate - A UTC date representing midnight in club timezone
 * @returns A local date representing the same calendar date
 */
export const convertClubTimeZoneDateToLocal = (utcDate: Date): Date => {
  if (!utcDate) return utcDate

  // Format the UTC date in club timezone to get the year/month/day
  const clubTZFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: MK_CLUB_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

  const parts = clubTZFormatter.formatToParts(utcDate)
  const year = parseInt(parts.find((p) => p.type === 'year')?.value || '0', 10)
  const month = parseInt(parts.find((p) => p.type === 'month')?.value || '0', 10) - 1 // month is 0-indexed
  const day = parseInt(parts.find((p) => p.type === 'day')?.value || '0', 10)

  // Create a local date at midnight for the same calendar date
  return new Date(year, month, day)
}

/**
 * Compares two dates to see if they represent the same calendar date in the club timezone.
 * Useful for comparing a local date (from calendar) with a UTC date (from context).
 *
 * @param date1 - First date (can be local or UTC)
 * @param date2 - Second date (can be local or UTC)
 * @returns true if both dates represent the same calendar date in club timezone
 */
export const isSameDateInClubTimeZone = (date1: Date | null | undefined, date2: Date | null | undefined): boolean => {
  if (!date1 || !date2) return false

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: MK_CLUB_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

  const parts1 = formatter.formatToParts(date1)
  const parts2 = formatter.formatToParts(date2)

  const year1 = parseInt(parts1.find((p) => p.type === 'year')?.value || '0', 10)
  const month1 = parseInt(parts1.find((p) => p.type === 'month')?.value || '0', 10)
  const day1 = parseInt(parts1.find((p) => p.type === 'day')?.value || '0', 10)

  const year2 = parseInt(parts2.find((p) => p.type === 'year')?.value || '0', 10)
  const month2 = parseInt(parts2.find((p) => p.type === 'month')?.value || '0', 10)
  const day2 = parseInt(parts2.find((p) => p.type === 'day')?.value || '0', 10)

  return year1 === year2 && month1 === month2 && day1 === day2
}

/**
 * Formats a Date object to YYYY-MM-DD string from calendar date components.
 * No timezone conversion - just extracts year/month/day and formats as ISO date string.
 *
 * For UTC dates (dates created with Date.UTC()), use the useUTC parameter to extract UTC components.
 *
 * @param date The Date object to format
 * @param useUTC If true, extracts UTC components (for UTC dates). Default: false (uses local timezone).
 * @returns Date string in YYYY-MM-DD format
 */
export const formatDateAsISO = (date: Date | null | undefined, useUTC: boolean = false): string => {
  if (!date) return ''
  if (useUTC) {
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/**
 * Compares two dates by their calendar date components (year/month/day).
 * No timezone conversion - extracts calendar date from each date and compares directly.
 * Works with both local dates and UTC dates by using appropriate getters.
 *
 * @param date1 The first date to compare (can be local or UTC)
 * @param date2 The second date to compare (can be local or UTC)
 * @param useUTCForDate1 If true, uses UTC getters for date1 (default: false, uses local getters)
 * @param useUTCForDate2 If true, uses UTC getters for date2 (default: false, uses local getters)
 * @returns true if the calendar dates match, false otherwise
 */
export const doCalendarDatesMatch = (date1: Date | null | undefined, date2: Date | null | undefined, useUTCForDate1: boolean = false, useUTCForDate2: boolean = false): boolean => {
  if (!date1 || !date2) return false

  const year1 = useUTCForDate1 ? date1.getUTCFullYear() : date1.getFullYear()
  const month1 = useUTCForDate1 ? date1.getUTCMonth() : date1.getMonth()
  const day1 = useUTCForDate1 ? date1.getUTCDate() : date1.getDate()

  const year2 = useUTCForDate2 ? date2.getUTCFullYear() : date2.getFullYear()
  const month2 = useUTCForDate2 ? date2.getUTCMonth() : date2.getMonth()
  const day2 = useUTCForDate2 ? date2.getUTCDate() : date2.getDate()

  return year1 === year2 && month1 === month2 && day1 === day2
}

/**
 * Compares two UTC dates by their calendar date components (year/month/day).
 * Convenience wrapper for doCalendarDatesMatch with both dates using UTC getters.
 *
 * @param utcDate1 The first UTC date to compare
 * @param utcDate2 The second UTC date to compare
 * @returns true if the calendar dates match, false otherwise
 */
export const doUTCDatesMatch = (utcDate1: Date | null | undefined, utcDate2: Date | null | undefined): boolean => doCalendarDatesMatch(utcDate1, utcDate2, true, true)

/**
 * @deprecated Use formatDateInClubTimeZone instead. This function uses UTC which can cause date shifting.
 */
export const formatDate = (date: Date, format: string): string => date?.toISOString().split('T')[0]
